import { AdminUpdatePrompt } from './app-update.js'
import { useAtom } from 'jotai'
import { useLayoutEffect, useState, type ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { AdminSidebar, AdminSidebarTrigger } from './components/admin-sidebar'
import {
  AdminBreadcrumbs,
  useAdminParentDestination,
} from './components/admin-breadcrumbs'
import { PageHeaderTargetProvider } from './components/page-header'
import { buttonVariants, Icon, SidebarInset, SidebarProvider } from '@speaking-coach/ui'
import { activeNavigationItem } from './navigation/admin-navigation'
import { OperatorFaqProvider } from './operator-faq/operator-faq'
import { sidebarCollapsedAtom } from './state/layout'

function ScrollToRouteTop(): null {
  const { pathname } = useLocation()

  useLayoutEffect(() => {
    // A new Admin page starts at its beginning. Query-string changes are
    // intentionally excluded: paging or filtering the current list should
    // not move the operator away from the table they are working in.
    window.scrollTo(0, 0)
  }, [pathname])

  return null
}

function ParentClose(): JSX.Element | null {
  const { pathname, search } = useLocation()
  const parent = useAdminParentDestination(pathname, search)
  if (parent === null) return null

  return (
    <Link
      to={parent.to}
      aria-label={`Close and return to ${parent.label}`}
      title={`Return to ${parent.label}`}
      data-slot="admin-parent-close"
      className={`${buttonVariants({ variant: 'ghost', size: 'icon' })} shrink-0`}
    >
      <Icon name="close" />
    </Link>
  )
}

function TopLevelSectionIcon(): JSX.Element | null {
  const { pathname, search } = useLocation()
  if (useAdminParentDestination(pathname, search) !== null) return null

  const item = activeNavigationItem(pathname)
  if (item === undefined) return null
  return (
    <Icon
      name={item.icon}
      size={14}
      data-slot="admin-section-icon"
      className="shrink-0 text-text-faint"
    />
  )
}

export function AdminShell({ children }: { children: ReactNode }): JSX.Element {
  const [collapsed, setCollapsed] = useAtom(sidebarCollapsedAtom)
  const [pageHeaderTarget, setPageHeaderTarget] = useState<HTMLDivElement | null>(
    null,
  )

  return (
    <SidebarProvider
      narrowQuery="(max-width: 1023px)"
      open={!collapsed}
      onOpenChange={(open) => setCollapsed(!open)}
    >
      <OperatorFaqProvider>
        <PageHeaderTargetProvider target={pageHeaderTarget}>
          <ScrollToRouteTop />
          <AdminSidebar />
          <SidebarInset>
            <header
              data-slot="admin-header"
              className="sticky top-0 z-40 flex h-14 shrink-0 items-center gap-3 border-b border-border-default bg-surface-card px-4"
            >
              <AdminSidebarTrigger />
              <div
                data-slot="admin-header-content"
                className="flex min-w-0 flex-1 items-center gap-2"
              >
                <AdminBreadcrumbs />
                <TopLevelSectionIcon />
                <div ref={setPageHeaderTarget} className="min-w-0 flex-1" />
              </div>
              <ParentClose />
            </header>
            <AdminUpdatePrompt />
            <div className="min-w-0 flex-1">{children}</div>
          </SidebarInset>
        </PageHeaderTargetProvider>
      </OperatorFaqProvider>
    </SidebarProvider>
  )
}
